/**
 * Turns a provisioning failure - an orchestrator HTTP error, a network
 * failure reaching it, or a Job's stored failure reason - into a message a
 * user can act on, instead of a raw JSON body or a bare "fetch failed".
 *
 * Most handler-rejected requests share a flat `{"detail": "..."}` body, and
 * a Job's stored failure is whatever string the worker's Pulumi run produced.
 * Policy admission is the exception: its stable `PolicyDenied` problem carries
 * caller-safe violations, which policyDenialFailure renders without guessing
 * from keywords or evaluating the policy in Backstage.
 */

export type FailureKind = 'user' | 'platform';

export type FailureClassification = {
  /** `user`: the requester can fix it. `platform`: it needs an operator. */
  kind: FailureKind;
  /** Short, human label for the failure - what to show as a heading. */
  title: string;
  /** The failure reason itself, in one sentence a non-operator can read. */
  detail: string;
  /** What the reader should do about it. */
  remediation: string;
};

export type FailureSignal = {
  /** The response's HTTP status, when a response was actually received. */
  status?: number;
  /**
   * The `fetch` failure cause, when no response was received at all - Node's
   * `fetch` throws a bare `TypeError: fetch failed` and puts the actual
   * reason (`ECONNREFUSED`, `ENOTFOUND`, a timeout, ...) on `.cause`.
   */
  cause?: unknown;
  /** The human-readable reason, already pulled out of a response body. */
  detail?: string;
};

/**
 * Composes a classification into one readable sentence for a log line or a
 * thrown error.
 *
 * The orchestrator's own `detail` strings are fragments rather than sentences
 * ("authentication required", "404 page not found"), so concatenating them
 * raw runs the remediation straight onto the end of the reason. This
 * terminates the detail before appending, leaving the stored text itself
 * untouched for a caller that wants to render the parts separately.
 *
 * Pass `includeTitle: false` where the title is already shown separately - a
 * dialog heading, say - so it is not repeated in the body.
 */
export function formatFailure(
  classification: FailureClassification,
  options: { includeTitle?: boolean } = {},
): string {
  const { title, detail, remediation } = classification;
  const terminated = /[.!?]$/.test(detail.trim())
    ? detail.trim()
    : `${detail.trim()}.`;
  const body = `${terminated} ${remediation}`;
  return options.includeTitle === false ? body : `${title}: ${body}`;
}

const NOT_FOUND_CODES = new Set(['ECONNREFUSED', 'ENOTFOUND', 'EAI_AGAIN']);
const TIMEOUT_CODES = new Set([
  'ETIMEDOUT',
  'UND_ERR_CONNECT_TIMEOUT',
  'UND_ERR_HEADERS_TIMEOUT',
  'UND_ERR_BODY_TIMEOUT',
]);

const CONTACT_OPS = 'Contact platform operations.';

/**
 * Pulls a human-readable reason out of a parsed JSON error body, recognising
 * the two shapes this platform's backends actually return: the orchestrator's
 * flat `{"detail": "..."}` (see `helpers.go`), and the generic Backstage
 * backend envelope `{"error": {"message": "..."}}` that the Scaffolder
 * backend (and any other Backstage backend) raises errors through.
 *
 * `body` may also be raw text: a gateway or proxy that rejects a request
 * before it reaches either backend (a 502/504 from Traefik, say) answers with
 * an HTML error page, not JSON, and that is never a reason to show the reader
 * a page of markup - this returns undefined instead of raw text that is not
 * evidently a plain, short message.
 */
export function parseOrchestratorDetail(body: unknown): string | undefined {
  if (typeof body === 'string') {
    const trimmed = body.trim();
    if (!trimmed) {
      return undefined;
    }
    try {
      return parseOrchestratorDetail(JSON.parse(trimmed));
    } catch {
      // Not JSON. A short plain-text body is still worth showing; markup
      // from a proxy's own error page is not.
      return trimmed.startsWith('<') || trimmed.length > 500
        ? undefined
        : trimmed;
    }
  }
  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>;
    if (typeof record.detail === 'string' && record.detail.trim()) {
      return record.detail.trim();
    }
    const error = record.error;
    if (error && typeof error === 'object') {
      const message = (error as Record<string, unknown>).message;
      if (typeof message === 'string' && message.trim()) {
        return message.trim();
      }
    }
  }
  return undefined;
}

/** Present the caller-safe diagnostics in an Orchestrator policy admission
 * problem. The policy engine owns the decision; Scaffolder only renders the
 * structured 422 response and never attempts to evaluate a policy itself. */
export function policyDenialFailure(
  body: unknown,
): FailureClassification | undefined {
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    return undefined;
  }
  const problem = body as Record<string, unknown>;
  if (problem.code !== 'PolicyDenied') {
    return undefined;
  }

  const oneLine = (value: unknown, maxLength = 200): string | undefined => {
    if (typeof value !== 'string') {
      return undefined;
    }
    const text = value.replace(/\s+/g, ' ').trim();
    return text ? text.slice(0, maxLength) : undefined;
  };
  const violations = Array.isArray(problem.violations)
    ? problem.violations
    : [];
  const descriptions = violations.slice(0, 3).flatMap(value => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return [];
    }
    const finding = value as Record<string, unknown>;
    const policyRef = finding.policyRef;
    const policyId =
      policyRef && typeof policyRef === 'object'
        ? oneLine((policyRef as Record<string, unknown>).id, 100)
        : undefined;
    const ruleId = oneLine(finding.ruleId, 100);
    const fieldPath = oneLine(finding.fieldPath, 120);
    const message = oneLine(finding.message);
    if (!policyId && !ruleId && !message) {
      return [];
    }
    const reference = [
      policyId ? `policy ${policyId}` : 'mandatory policy',
      ruleId ? `rule ${ruleId}` : undefined,
      fieldPath ? `field ${fieldPath}` : undefined,
    ]
      .filter(Boolean)
      .join(', ');
    return [`${reference}: ${message ?? 'The request violates this rule.'}`];
  });
  const remaining = Math.max(violations.length - 3, 0);
  const decisionId = oneLine(problem.decisionId, 100);
  const detail = descriptions.length
    ? `${descriptions.join(' ')}${
        remaining ? ` ${remaining} more violation(s).` : ''
      }${decisionId ? ` Decision ID: ${decisionId}.` : ''}`
    : `${oneLine(problem.detail) ?? 'A mandatory policy denied this request.'}${
        decisionId ? ` Decision ID: ${decisionId}.` : ''
      }`;

  return {
    kind: 'user',
    title: 'Policy denied',
    detail,
    remediation:
      'Change the requested values to satisfy the rule, or ask an application group or tenant policy admin to review it.',
  };
}

function networkClassification(cause: unknown): FailureClassification {
  const code = errorCode(cause);
  if (code && NOT_FOUND_CODES.has(code)) {
    return {
      kind: 'platform',
      title: 'Provider unreachable',
      detail: 'Could not establish a connection to the destination service.',
      remediation: CONTACT_OPS,
    };
  }
  if ((code && TIMEOUT_CODES.has(code)) || isTimeoutLike(cause)) {
    return {
      kind: 'platform',
      title: 'Request timed out',
      detail: 'The destination service did not respond in time.',
      remediation: CONTACT_OPS,
    };
  }
  return {
    kind: 'platform',
    title: 'Network error',
    // No recognised error code, so there is nothing to translate into a
    // plainer sentence. The underlying message is then the only diagnosable
    // fact there is, and dropping it would leave the reader - who is being
    // told to contact platform operations - with nothing to report.
    detail: causeMessage(cause)
      ? `A network error prevented the request from completing: ${causeMessage(
          cause,
        )}`
      : 'A network error prevented the request from completing.',
    remediation: CONTACT_OPS,
  };
}

function causeMessage(cause: unknown): string | undefined {
  // Read by shape, not `instanceof Error`: an error that crossed a realm
  // boundary is still an error and still worth reporting.
  const raw = (cause as { message?: unknown } | null)?.message;
  let message: string | undefined;
  if (typeof raw === 'string') {
    message = raw;
  } else if (typeof cause === 'string') {
    message = cause;
  }
  const trimmed = message?.trim();
  if (!trimmed || trimmed.length > 200) {
    return undefined;
  }
  // `fetch failed` is the placeholder this whole branch exists to replace.
  return /^fetch failed$/i.test(trimmed) ? undefined : trimmed;
}

function errorCode(cause: unknown): string | undefined {
  if (cause && typeof cause === 'object' && 'code' in cause) {
    const code = (cause as { code?: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

function isTimeoutLike(cause: unknown): boolean {
  const name = (cause as { name?: unknown } | null)?.name;
  if (name === 'AbortError') {
    return true;
  }
  const message = (cause as { message?: unknown } | null)?.message;
  return typeof message === 'string' && /timeout/i.test(message);
}

/**
 * Keyword overrides read from the orchestrator's own message text. Never
 * reached for a 5xx or a network failure - see the precedence note on
 * `classifyFailure` - because incidental overlap in an operator-facing
 * message must not soften a genuine platform failure into one the classifier
 * tells the user to go and fix.
 *
 * These are also the sole classifier for a signal that carries text but no
 * status at all, which is what a failed provisioning job reports.
 */
const DETAIL_OVERRIDES: {
  pattern: RegExp;
  classification: Omit<FailureClassification, 'detail'>;
}[] = [
  {
    pattern: /manual[- ]cleanup/i,
    classification: {
      kind: 'platform',
      title: 'Manual cleanup required',
      remediation:
        'Contact platform operations to finish cleanup before this name can be reused.',
    },
  },
  {
    pattern: /already in progress/i,
    classification: {
      kind: 'user',
      title: 'Operation already in progress',
      remediation: 'Wait for the current operation to finish, then retry.',
    },
  },
  {
    // Must precede /quota/: an unknown quota fact names the quota but is not
    // an exceeded one, and resizing the order cannot fix it.
    pattern: /PolicyEvaluationUnknown/,
    classification: {
      kind: 'platform',
      title: 'Policy check unavailable',
      remediation: CONTACT_OPS,
    },
  },
  {
    pattern: /quota/i,
    classification: {
      kind: 'user',
      title: 'Quota exceeded',
      remediation: 'Request a quota increase or choose a smaller size.',
    },
  },
  {
    pattern: /(capacity|storage|disk space|out of space)/i,
    classification: {
      kind: 'user',
      title: 'Insufficient capacity',
      remediation: 'Select another storage tier or a smaller size.',
    },
  },
  {
    pattern: /already exists/i,
    classification: {
      kind: 'user',
      title: 'Name already in use',
      remediation:
        'Choose a different name, or update the existing resource instead.',
    },
  },
];

/**
 * Classifies a provisioning failure from whatever signal is available: a
 * network `cause` when no response ever arrived, or a response `status` plus
 * whatever `detail` text (from `parseOrchestratorDetail`) came with it.
 *
 * Precedence, deliberately: a network failure or a 5xx status always means
 * `platform`, no matter what the message text says - only after that is
 * keyword text allowed to refine an otherwise ambiguous 4xx into a more
 * specific bucket than its status code alone would give.
 */
export function classifyFailure(signal: FailureSignal): FailureClassification {
  const { status, cause, detail } = signal;

  if (cause !== undefined) {
    return networkClassification(cause);
  }

  if (status !== undefined && status >= 500) {
    const unreachable = status === 502 || status === 503 || status === 504;
    return {
      kind: 'platform',
      title: unreachable ? 'Provider unreachable' : 'Provider error',
      detail:
        detail ??
        (unreachable
          ? 'The provisioning service is temporarily unavailable.'
          : 'The provisioning service encountered an unexpected error.'),
      remediation: CONTACT_OPS,
    };
  }

  // A missing security exception (#16107) is granted by the platform team, not
  // by a tenant admin, so the generic operator-access hint would send the
  // tenant to the wrong person.
  if (status === 403 && detail && /security exception/i.test(detail)) {
    return {
      kind: 'user',
      title: 'Security exception required',
      detail,
      remediation:
        'Ask the platform team to grant this exception to your tenant and application group.',
    };
  }

  if (status === 401 || status === 403) {
    return {
      kind: 'user',
      title: 'Not authorized',
      detail: detail ?? 'You are not authorized to perform this action.',
      remediation:
        'Ask your tenant admin for operator access on this application group.',
    };
  }

  if (detail) {
    const override = DETAIL_OVERRIDES.find(({ pattern }) =>
      pattern.test(detail),
    );
    if (override) {
      return { ...override.classification, detail };
    }
  }

  if (status === 400 || status === 422) {
    return {
      kind: 'user',
      title: 'Invalid request',
      detail: detail ?? 'The request did not pass validation.',
      remediation: 'Correct the highlighted input and try again.',
    };
  }

  if (status === 404) {
    return {
      kind: 'user',
      title: 'Not found',
      detail: detail ?? 'The requested resource does not exist.',
      remediation: 'Check the name and try again.',
    };
  }

  if (status === 409) {
    return {
      kind: 'user',
      title: 'Conflict',
      detail: detail ?? 'The request conflicts with the current state.',
      remediation:
        'Choose a different name, or wait for the in-progress operation to finish.',
    };
  }

  if (status === 429) {
    return {
      kind: 'user',
      title: 'Too many requests',
      detail: detail ?? 'The request was rate-limited.',
      remediation:
        'Wait a moment and try again, or request a quota increase if this persists.',
    };
  }

  if (detail) {
    return {
      kind: 'platform',
      title: 'Request failed',
      detail,
      remediation: CONTACT_OPS,
    };
  }

  return {
    kind: 'platform',
    title: 'Unknown error',
    detail: 'The request failed for an unknown reason.',
    remediation: CONTACT_OPS,
  };
}
